import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { useToast } from "../../components/Toast";
import { formatTaipeiDateTime } from "../../lib/datetime";

// 設定 → 打卡機(業主 2026-09-25)。**老闆專屬。**
//
// 門口打卡機上只點名字就打卡,守門的是機器本身(後端 routers/kiosk.py)。
// 這一頁列出啟用過的機器,可以停用 —— 機器掉了、換新的,就從這裡把舊的那台關掉。
// 啟用不在這裡:要在那台機器上打開 /quote/kiosk 按,因為通行證是種在那台機器上的。

interface Device {
  id: number;
  name: string;
  created_at: string;
  revoked_at: string | null;
}

export function KioskDevices() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const devices = useQuery({
    queryKey: ["kiosk-devices"],
    queryFn: () => api.get<Device[]>("/kiosk/devices"),
  });
  const revoke = useMutation({
    mutationFn: (id: number) => api.post(`/kiosk/devices/${id}/revoke`),
    onSuccess: () => {
      toast.push("已停用,那台機器從此打不了卡", "success");
      void queryClient.invalidateQueries({ queryKey: ["kiosk-devices"] });
    },
    onError: (err: unknown) => toast.push(err instanceof Error ? err.message : "停用不了", "error"),
  });
  const rows = Array.isArray(devices.data) ? devices.data : [];

  return (
    <div className="flex w-full flex-col gap-3">
      <p className="m-0 text-sm text-[var(--text-2)]">
        要讓一台機器變成打卡機:在那台機器上用老闆帳號登入,打開 <code>/quote/kiosk</code>,
        按「啟用這台為打卡機」。之後登出也照常能打卡。機器掉了或換新的,就在這裡停用舊的那台。
      </p>
      {devices.isLoading ? <p className="m-0 text-sm text-[var(--text-3)]">載入中…</p> : null}
      {!devices.isLoading && rows.length === 0 ? (
        <p className="m-0 text-sm text-[var(--text-3)]">還沒有啟用任何一台。</p>
      ) : null}
      {rows.length > 0 ? (
        <table aria-label="打卡機" className="w-full border-collapse text-sm">
          <thead>
            <tr className="text-left text-[var(--text-2)]">
              <th className="px-3 py-2 font-medium">名稱</th>
              <th className="px-3 py-2 font-medium">啟用時間</th>
              <th className="px-3 py-2 font-medium">狀態</th>
              <th className="px-3 py-2 font-medium">動作</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((device) => (
              <tr key={device.id} className="border-t border-[var(--border)]">
                <td className="px-3 py-2 text-[var(--text)]">{device.name}</td>
                <td className="tabular px-3 py-2 text-[var(--text-2)]">{formatTaipeiDateTime(device.created_at)}</td>
                <td className="px-3 py-2">
                  {device.revoked_at ? <Badge tone="neutral">已停用</Badge> : <Badge tone="ok">使用中</Badge>}
                </td>
                <td className="px-3 py-2">
                  {device.revoked_at ? null : (
                    <Button
                      size="sm"
                      variant="secondary"
                      aria-label={`停用 ${device.name}`}
                      onClick={() => {
                        if (window.confirm(`停用「${device.name}」嗎?那台機器之後就打不了卡。`)) {
                          revoke.mutate(device.id);
                        }
                      }}
                    >
                      停用
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}
